import { Box, Grid } from '@chronosphereio/chrono-ui';
import type { GaugeSeriesOption } from 'echarts';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { GaugeChartBase, GaugeSeries, useChartsTheme } from '@/components';
import { ContentDimensions } from '@/core';
import { useFindThreshold } from '@/core/utils/thresholds';
import { useDataQueryResult } from '@/plugin-system';
import { convertThresholds, defaultThresholdInput } from '../../model/thresholds';
import { GaugeChartPanelProps } from './GaugeChartPanel';
import { GaugeChartVirtualizedGrid } from './GaugeChartVirtualizedGrid';
import { DEFAULT_MAX_PERCENT, DEFAULT_UNIT } from './gauge-chart-model';
import { GAUGE_MIN_WIDTH, useGaugeGridLayout } from './layout';
import { sortSeriesData } from './utils';

const EMPTY_GAUGE_SERIES: GaugeSeries = { label: '', value: undefined };

/**
 * When greater than this number of series, react-virtuoso will be used to improve performance.
 */
const VIRTUALIZATION_THRESHOLD = 10;

export type GaugeChartNonNullableThresholds = NonNullable<GaugeChartPanelLoadedProps['thresholds']>;

type GaugeChartPanelLoadedProps = Omit<GaugeChartPanelProps, keyof ContentDimensions> & {
  contentDimensions: ContentDimensions;
};

export function GaugeChartPanelLoaded(props: GaugeChartPanelLoadedProps) {
  const { max, contentDimensions, sort } = props;

  const { thresholds: thresholdsColors } = useChartsTheme();

  const { queryResults } = useDataQueryResult({ dataTypeFilter: 'SingleValue' });

  // ensures all default unit properties set if undef
  const unit = useMemo(() => {
    return merge({}, DEFAULT_UNIT, props.unit);
  }, [props.unit]);

  const thresholds = props.thresholds ?? (defaultThresholdInput as GaugeChartNonNullableThresholds);

  const gaugeData: GaugeSeries[] = useMemo(() => {
    const seriesData: GaugeSeries[] = [];

    const resultsWithData = queryResults.filter((result) => result.data !== undefined);

    for (let i = 0; i < resultsWithData.length; i++) {
      const result = queryResults[i];

      if (result === undefined) {
        break;
      }

      if (!result.data) continue;

      for (const data of result.data.values) {
        const series = {
          value: data.value,
          label: data.formattedName || data.name,
        };
        seriesData.push(series);
      }
    }

    return sortSeriesData(seriesData, sort);
  }, [queryResults, sort]);

  const dataMax = useMemo(() => {
    return gaugeData.reduce((currentMax, series) => Math.max(series.value || 0, currentMax), 0);
  }, [gaugeData]);

  // needed for end value of last threshold color segment
  let thresholdMax: number;
  if (max === undefined) {
    if (unit.kind === 'Percent') {
      thresholdMax = DEFAULT_MAX_PERCENT;
    } else {
      // If data max is less than one, then we'll assume it's 0-1 percent so we'll set
      // the max to 1.  Otherwise, we will use whatever the max value is as the max
      thresholdMax = dataMax < 1 ? 1 : dataMax;
    }
  } else {
    thresholdMax = max;
  }

  const axisLine: GaugeSeriesOption['axisLine'] = useMemo(() => {
    const axisLineColors = convertThresholds(thresholds, thresholdMax, thresholdsColors);

    return {
      show: true,
      lineStyle: {
        width: 5,
        color: axisLineColors,
      },
    };
  }, [thresholdMax, thresholds, thresholdsColors]);

  const findThreshold = useFindThreshold(thresholds);

  const { hasMultipleCharts, width, height, virtualizedChartSize } = useGaugeGridLayout(
    gaugeData.length,
    contentDimensions
  );

  if (gaugeData.length === 0) {
    return (
      <Box width={contentDimensions.width} height={contentDimensions.height}>
        {/* no data message handled inside chart component */}
        <GaugeChartBase
          width={contentDimensions.width}
          height={contentDimensions.height}
          data={EMPTY_GAUGE_SERIES}
          unit={unit}
          axisLine={axisLine}
          max={thresholdMax}
        />
      </Box>
    );
  }

  return (
    <Box width={contentDimensions.width} height={contentDimensions.height} overflow={'auto'}>
      {gaugeData.length > VIRTUALIZATION_THRESHOLD ? (
        <GaugeChartVirtualizedGrid
          gaugeData={gaugeData}
          contentWidth={contentDimensions.width}
          gaugeMinWidth={GAUGE_MIN_WIDTH}
          chartSize={virtualizedChartSize}
          unit={unit}
          axisLine={axisLine}
          thresholdMax={thresholdMax}
          thresholds={thresholds}
          hasMultipleCharts={hasMultipleCharts}
        />
      ) : (
        <Grid container spacing={hasMultipleCharts ? 2 : 0} alignItems="center" justifyContent="center">
          {gaugeData.map((series, seriesIndex) => {
            const threshold = findThreshold(series.value || 0);
            return (
              <Grid item key={`gauge-series-${seriesIndex}`}>
                <GaugeChartBase
                  width={width}
                  height={height}
                  data={series}
                  unit={unit}
                  axisLine={axisLine}
                  max={thresholdMax}
                  hideValue={threshold.show_value === false}
                  message={threshold.name}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
